import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PlusIcon, TrashIcon } from '@/components/icons';
import {
  parseUrlSegments,
  type MCPClassFormState,
  type UrlSegment,
} from '@/pages/mcp/mcp-class-form.utils';

interface MCPClassFormProps {
  form: MCPClassFormState;
  onChange: (next: MCPClassFormState) => void;
}

export function MCPClassForm({ form, onChange }: MCPClassFormProps) {
  const setField = <K extends keyof MCPClassFormState>(key: K, value: MCPClassFormState[K]) =>
    onChange({ ...form, [key]: value });

  const handleServerUrlChange = (url: string) => {
    const newSegments = parseUrlSegments(url);
    const merged: UrlSegment[] = newSegments.map((seg, i) => {
      const prev = form.urlSegments[i];
      if (prev && prev.value === seg.value) {
        return prev;
      }
      return seg;
    });
    onChange({ ...form, serverUrl: url, urlSegments: merged });
  };

  const updateSegment = (index: number, patch: Partial<UrlSegment>) => {
    const next = [...form.urlSegments];
    next[index] = { ...next[index], ...patch };
    onChange({ ...form, urlSegments: next });
  };

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <Card className="lg:col-span-1 self-start">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">基本信息</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            类的名称与描述用于在列表中识别与检索。
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-sm font-medium">名称</label>
            <Input
              value={form.name}
              onChange={(e) => setField('name', e.target.value)}
              placeholder="如: github-mcp"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium">描述</label>
            <Textarea
              value={form.description}
              onChange={(e) => setField('description', e.target.value)}
              rows={4}
            />
          </div>
        </CardContent>
      </Card>

      <Card className="lg:col-span-2">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">模板（默认值）</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            实例化时若未提供对应参数，会回落到这里的默认值。
          </p>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Server URL</label>
            <Input
              value={form.serverUrl}
              onChange={(e) => handleServerUrlChange(e.target.value)}
              placeholder="https://api.example.com/mcp"
            />
            <p className="text-xs text-muted-foreground">
              传输方式默认 Streamable HTTP，绝大多数远端 MCP 走此协议。
            </p>
          </div>

          {form.urlSegments.length > 0 && (
            <div className="space-y-2">
              <div>
                <label className="text-sm font-medium">URL 路径参数</label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  打开「可配置」开关，该路径段在实例化时可覆盖。
                </p>
              </div>
              <div className="border rounded-md overflow-hidden">
                <table className="w-full text-sm table-fixed">
                  <thead className="bg-muted/40 text-xs">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">默认值</th>
                      <th className="px-3 py-2 text-left font-medium w-[80px]">可配置</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {form.urlSegments.map((seg, i) => (
                      <tr key={i} className="group hover:bg-muted/20 h-10">
                        <td className="px-3 py-1.5">
                          <code className="text-xs bg-muted px-1.5 py-0.5 rounded font-mono">
                            {seg.defaultValue || seg.value}
                          </code>
                        </td>
                        <td className="px-3 py-1.5">
                          <button
                            type="button"
                            title={seg.configurable ? '关闭可配置' : '设为可配置参数'}
                            onClick={() => {
                              if (seg.configurable) {
                                updateSegment(i, {
                                  configurable: false,
                                  value: seg.defaultValue || seg.value,
                                });
                              } else {
                                updateSegment(i, { configurable: true, defaultValue: seg.value });
                              }
                            }}
                            className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${
                              seg.configurable ? 'bg-primary' : 'bg-muted-foreground/30'
                            }`}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                seg.configurable ? 'translate-x-4' : 'translate-x-0.5'
                              }`}
                            />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-x-6 gap-y-4">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium whitespace-nowrap">超时时间:</label>
              <div className="relative">
                <Input
                  type="number"
                  min={0}
                  value={form.timeoutSeconds || ''}
                  onChange={(e) =>
                    setField(
                      'timeoutSeconds',
                      e.target.value === '' ? 0 : Math.max(0, Number(e.target.value))
                    )
                  }
                  placeholder="0"
                  className="h-8 w-24 pr-8 text-sm"
                />
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">
                  秒
                </span>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setField('skipTlsVerify', !form.skipTlsVerify)}
              className="flex items-center gap-2 group"
              title="开启后将跳过 TLS 证书校验，仅用于自签证书或内网调试"
            >
              <span
                className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${
                  form.skipTlsVerify ? 'bg-destructive' : 'bg-muted-foreground/30'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    form.skipTlsVerify ? 'translate-x-4' : 'translate-x-0.5'
                  }`}
                />
              </span>
              <span className="text-sm group-hover:text-foreground">跳过 TLS 校验</span>
              {form.skipTlsVerify && <span className="text-xs text-destructive">⚠ 仅限调试</span>}
            </button>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium">Headers</label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  打开「可配置」开关，header 会作为参数（target ={' '}
                  <code className="bg-muted px-1 rounded">headers.&lt;名称&gt;</code>
                  ），实例化时可覆盖。
                </p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {form.headers.length === 0 && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs"
                    onClick={() =>
                      setField('headers', [
                        {
                          key: 'Authorization',
                          value: 'Bearer ',
                          configurable: true,
                        },
                      ])
                    }
                  >
                    + Bearer 预设
                  </Button>
                )}
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-7 text-xs"
                  onClick={() =>
                    setField('headers', [
                      ...form.headers,
                      { key: '', value: '', configurable: false },
                    ])
                  }
                >
                  <PlusIcon className="h-3 w-3 mr-1" />
                  添加
                </Button>
              </div>
            </div>

            {form.headers.length === 0 ? (
              <div className="rounded-md border border-dashed bg-muted/20 px-4 py-6 text-center">
                <p className="text-sm text-muted-foreground">暂无 header</p>
                <p className="text-xs text-muted-foreground/70 mt-1">
                  大多数远端 MCP 至少需要 Authorization 头
                </p>
              </div>
            ) : (
              <div className="border rounded-md overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40 text-xs">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium w-1/3">名称</th>
                      <th className="px-3 py-2 text-left font-medium">值</th>
                      <th className="px-3 py-2 text-left font-medium w-24">可配置</th>
                      <th className="w-9" />
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {form.headers.map((h, i) => (
                      <tr key={i} className="group hover:bg-muted/20">
                        <td className="px-2 py-1.5">
                          <Input
                            placeholder="Header 名"
                            value={h.key}
                            onChange={(e) => {
                              const next = [...form.headers];
                              next[i] = { ...next[i], key: e.target.value };
                              setField('headers', next);
                            }}
                            className="h-7 text-sm border-0 shadow-none focus-visible:ring-1 px-2"
                          />
                        </td>
                        <td className="px-2 py-1.5">
                          <Input
                            placeholder="值（可在实例化时覆盖）"
                            value={h.value}
                            onChange={(e) => {
                              const next = [...form.headers];
                              next[i] = { ...next[i], value: e.target.value };
                              setField('headers', next);
                            }}
                            className="h-7 text-sm border-0 shadow-none focus-visible:ring-1 px-2 font-mono"
                          />
                        </td>
                        <td className="px-3 py-1.5">
                          <button
                            type="button"
                            title={h.configurable ? '关闭可配置' : '设为可配置参数'}
                            onClick={() => {
                              const next = [...form.headers];
                              next[i] = {
                                ...next[i],
                                configurable: !next[i].configurable,
                              };
                              setField('headers', next);
                            }}
                            className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${
                              h.configurable ? 'bg-primary' : 'bg-muted-foreground/30'
                            }`}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                h.configurable ? 'translate-x-4' : 'translate-x-0.5'
                              }`}
                            />
                          </button>
                        </td>
                        <td className="px-1 py-1.5">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={() => {
                              const next = [...form.headers];
                              next.splice(i, 1);
                              setField('headers', next);
                            }}
                          >
                            <TrashIcon className="h-3.5 w-3.5" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
